import { useEffect, useState } from 'react';
import { getPublicDrawsList } from '../../api/draws';
import { getPublicMemberships } from '../../api/packages';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/routes';
import CustomerTopNav from '../../components/customerPortal/CustomerTopNav';
import '../../styles/customerPortal.css';
import CustomerFooter from '../../components/customerPortal/CustomerFooter';

interface DiscountItem {
  title: string;
  code: string;
  type?: string;
  drawId: string;
  sourceName: string;
  label?: string;
}

const Perks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [discounts, setDiscounts] = useState<DiscountItem[]>([]);
  const [copiedCode, setCopiedCode] = useState<string>('');

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000); // Hide after 2 seconds
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  useEffect(() => {
    if ((window as any).__perksFetchInProgress__) return;
    (window as any).__perksFetchInProgress__ = true;
    const fetchPerks = async () => {
      try {
        setLoading(true);
        const offers: DiscountItem[] = [];

        // Get all public draws with discounts
        try {
          const drawsResp = await getPublicDrawsList({ page: 1, pageSize: 100 });
          const drawsData = drawsResp?.data?.draws || drawsResp?.data || [];
          
          console.log('Fetched draws:', drawsData);
          
          for (const draw of drawsData) {
            if (draw.discounts && Array.isArray(draw.discounts) && draw.discounts.length > 0) {
              draw.discounts.forEach((d: any) => {
                const title = d.label || d.title || d.name || (d.type ? `${String(d.type)} discount` : 'Discount');
                offers.push({ 
                  title, 
                  code: d.code, 
                  type: d.type, 
                  drawId: draw.id, 
                  sourceName: draw.name || 'Draw',
                  label: d.label
                });
              });
            }
          }
        } catch (drawError) {
          console.warn('Failed to fetch draws:', drawError);
        }

        // Get all public memberships with discounts
        try {
          const membershipsResp = await getPublicMemberships({ page: 1, pageSize: 100 });
          const membershipsData = membershipsResp?.data?.memberships || membershipsResp?.data || [];
          
          console.log('Fetched memberships:', membershipsData);
          
          for (const membership of membershipsData) {
            if (membership.discounts && Array.isArray(membership.discounts) && membership.discounts.length > 0) {
              membership.discounts.forEach((d: any) => {
                const title = d.label || d.title || d.name || (d.type ? `${String(d.type)} discount` : 'Discount');
                offers.push({ 
                  title, 
                  code: d.code, 
                  type: d.type, 
                  drawId: membership.id, 
                  sourceName: membership.name || 'Membership',
                  label: d.label
                });
              });
            }
          }
        } catch (membershipError) {
          console.warn('Failed to fetch memberships:', membershipError);
        }

        console.log('Final offers:', offers);
        setDiscounts(offers);
      } catch (e: any) {
        setError(e?.message || 'Failed to load perks');
      } finally {
        setLoading(false);
      }
    };

    fetchPerks();
    return () => {
      (window as any).__perksFetchInProgress__ = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="perks-page bg-gray-50 min-h-screen">
      <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading perks...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="perks-page bg-gray-50 min-h-screen">
      <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading perks</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="perks-page bg-gray-50 min-h-screen">
      {/* Toast Notification */}
      {copiedCode && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 transform transition-all duration-300 ease-in-out animate-bounce">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Copied!</span>
        </div>
      )}

      {/* Header Section */}
      <CustomerTopNav />

      {/* Main Content */}
      <div className="p-6">
        <div className="w-full">
          {/* Header Section */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Exclusive Perks & Discounts</h1>
                  <p className="text-gray-600">Unlock special offers and discounts from our partner draws</p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{discounts.length}</div>
                      <div className="text-sm text-gray-500">Available Perks</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0v2m0 4v.01M4.93 4.93l1.414 1.414M17.657 17.657l1.414 1.414M2 12h2m16 0h2M4.93 19.07l1.414-1.414M17.657 6.343l1.414-1.414" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">100%</div>
                      <div className="text-sm text-gray-500">Free to Use</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">Instant</div>
                      <div className="text-sm text-gray-500">Activation</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Discounts Grid */}
          {discounts.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No perks available</h3>
                <p className="text-gray-500 mb-6">Check back later for new discounts and offers!</p>
                <button
                  onClick={() => navigate(PATHS.CUSTOMER_PORTAL)}
                  className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                >
                  Back to Portal
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {discounts.map((discount, index) => (
                <div key={`${discount.drawId}-${index}`} className="group bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden">
                  {/* Card Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                       
                      {discount.type && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          {discount.type}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{discount.title}</h3>
                    {discount.label && discount.title !== discount.label && (
                      <p className="text-sm text-violet-600 font-medium mb-2">{discount.label}</p>
                    )}
                    <p className="text-sm text-gray-500">From: {discount.sourceName}</p>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        {discount.type === 'affiliate' ? 'Affiliate Link' : 'Discount Code'}
                      </label>
                      {discount.type === 'affiliate' ? (
                        <a 
                          href={discount.code} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 transition-colors text-center"
                        >
                          Visit Affiliate Link
                        </a>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm font-semibold text-gray-900">
                            {discount.code}
                          </div>
                          <button
                            onClick={() => handleCopyCode(discount.code)}
                            className={`p-3 rounded-lg transition-all duration-200 ${
                              copiedCode === discount.code 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
                            }`}
                            title={copiedCode === discount.code ? "Copied!" : "Copy code"}
                          >
                            {copiedCode === discount.code ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    <CustomerFooter />
    </div>
  );
};

export default Perks;